// The workspace theme: a primary and an accent hex colour applied to the shell through the CSS variables
// the Tailwind preset reads. Dark mode is a class on <html>.

export interface Rgb {
  r: number
  g: number
  b: number
}

export function hexToRgb(hex: string): Rgb | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const n = parseInt(m[1]!, 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

const triplet = (c: Rgb) => `${c.r} ${c.g} ${c.b}`
const mix = (a: Rgb, b: Rgb, t: number): Rgb => ({
  r: Math.round(a.r + (b.r - a.r) * t),
  g: Math.round(a.g + (b.g - a.g) * t),
  b: Math.round(a.b + (b.b - a.b) * t),
})
const WHITE: Rgb = { r: 255, g: 255, b: 255 }
const BLACK: Rgb = { r: 0, g: 0, b: 0 }
const DARK_BG: Rgb = { r: 10, g: 16, b: 32 }

export interface ThemeColours {
  primary: string
  accent: string
}

export const DEFAULT_THEME: ThemeColours = { primary: '#0f7d6e', accent: '#d4a017' }

/** Writes the colour variables on <html>. Soft and text shades are derived for light and dark. */
export function applyTheme(
  theme: ThemeColours,
  root: HTMLElement | null = typeof document === 'undefined' ? null : document.documentElement
): void {
  if (!root) return
  const primary = hexToRgb(theme.primary) ?? hexToRgb(DEFAULT_THEME.primary)!
  const accent = hexToRgb(theme.accent) ?? hexToRgb(DEFAULT_THEME.accent)!
  const dark = root.classList.contains('dark')
  root.style.setProperty('--kit-primary-rgb', triplet(primary))
  root.style.setProperty('--kit-primary-dark-rgb', triplet(mix(primary, BLACK, 0.18)))
  root.style.setProperty(
    '--kit-primary-soft-rgb',
    triplet(dark ? mix(primary, DARK_BG, 0.75) : mix(primary, WHITE, 0.88))
  )
  root.style.setProperty(
    '--kit-primary-text-rgb',
    triplet(dark ? mix(primary, WHITE, 0.55) : mix(primary, BLACK, 0.3))
  )
  root.style.setProperty('--kit-accent-rgb', triplet(accent))
  root.style.setProperty(
    '--kit-accent-soft-rgb',
    triplet(dark ? mix(accent, DARK_BG, 0.75) : mix(accent, WHITE, 0.85))
  )
  root.style.setProperty(
    '--kit-accent-text-rgb',
    triplet(dark ? mix(accent, WHITE, 0.3) : mix(accent, BLACK, 0.35))
  )
}

export type ColourScheme = 'light' | 'dark'

export function applyColourScheme(
  scheme: ColourScheme,
  root: HTMLElement | null = typeof document === 'undefined' ? null : document.documentElement
): void {
  if (!root) return
  root.classList.toggle('dark', scheme === 'dark')
}

export function preferredColourScheme(): ColourScheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}
